import React, { ReactElement, useState } from 'react'
import Grid from '../Grid'
import Touchable from '../Touchable'
import { DropDownOption, DropDownProps } from './DropDownTypes'
import DropDownLabel from './components/DropDownLabel'
import DropDownOptionContainer from './components/DropDownOptionContainer'

const DropDown = <T,>({
    dropDownContainerStyle,
    dropDownTextStyle,
    onOptionChange,
    onDismiss,
    onOpen,
    optionContainerStyle,
    options,
    optionsTopComponent,
    optionsBottomComponent,
    optionStyle,
    optionTextStyle,
    renderDropDownIcon,
    scrollProps = {},
    selectedOption,
    testID,
}: DropDownProps<T>): ReactElement | null => {
    const [showOptions, setShowOptions] = useState(false)
    const [activeBlur, setActiveBlur] = useState(true)
    const [currentSelectedOption, setCurrentSelectedOption] = useState<DropDownOption<T>>(
        selectedOption || options[0]
    )
    const optionsContainer = React.useRef<HTMLDivElement>()
    const { border, borderColor, borderRadius, backgroundColor, ...containerStyle } =
        optionContainerStyle || {}
    const { scrollOffset, onScroll, scrollContentContainerStyle, showsVerticalScrollIndicator } =
        scrollProps

    React.useEffect(() => {
        if (selectedOption) setCurrentSelectedOption(selectedOption)
    }, [selectedOption])

    React.useEffect(() => {
        if (scrollOffset && showOptions && typeof optionsContainer.current?.scroll === 'function') {
            optionsContainer.current.scroll({ left: scrollOffset.x, top: scrollOffset.y })
        }
    }, [scrollOffset, showOptions])

    if (options.length === 0) return null

    const onDismissCallback = () => {
        onDismiss && onDismiss()
        setShowOptions(false)
    }

    const onLabelPress = (showOptions: boolean) => {
        if (showOptions) {
            onOpen && onOpen()
        } else {
            onDismiss && onDismiss()
        }
        setShowOptions(showOptions)
    }

    return (
        <Grid
            noExpand
            tabIndex={0}
            position="relative"
            onBlur={activeBlur ? onDismissCallback : undefined}
        >
            <DropDownLabel
                testID={testID}
                label={currentSelectedOption.label}
                onPress={(): void => onLabelPress(!showOptions)}
                dropDownContainerStyle={dropDownContainerStyle}
                dropDownTextStyle={dropDownTextStyle}
                renderDropDownIcon={(): ReactElement | null => {
                    return renderDropDownIcon ? renderDropDownIcon(showOptions) : null
                }}
            />
            {showOptions ? (
                <Grid
                    noExpand
                    zIndex={100}
                    position="absolute"
                    width="100%"
                    top="100%"
                    onMouseOver={(): void => setActiveBlur(false)}
                    onMouseLeave={(): void => setActiveBlur(true)}
                    {...containerStyle}
                >
                    <Grid
                        noExpand
                        borderRadius={borderRadius}
                        border={border}
                        borderColor={borderColor}
                        backgroundColor={backgroundColor}
                        ref={optionsContainer}
                        onScroll={onScroll}
                        {...(showsVerticalScrollIndicator
                            ? { overflowY: 'scroll', overflowX: 'auto' }
                            : { overflow: 'auto' })}
                        {...scrollContentContainerStyle}
                    >
                        {optionsTopComponent}
                        {options.map((option, index) => (
                            <Touchable
                                key={option.label}
                                onPress={(): void => {
                                    setCurrentSelectedOption(option)
                                    onOptionChange && onOptionChange(option)
                                    setShowOptions(false)
                                    setActiveBlur(true)
                                }}
                            >
                                <DropDownOptionContainer
                                    {...{
                                        option,
                                        index,
                                        optionStyle,
                                        optionTextStyle,
                                    }}
                                />
                            </Touchable>
                        ))}
                        {optionsBottomComponent}
                    </Grid>
                </Grid>
            ) : null}
        </Grid>
    )
}

export default DropDown
